import {
  InternalRefetchQueryDescriptor,
  useApolloClient,
} from "@apollo/client";
import {
  CollapseSection,
  ModalOpenButton,
  useLocalStorageAsState,
} from "@msys/ui";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { Grid, IconButton, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useOutletContext } from "react-router-dom";
import {
  DefineItemProps2Entry,
  DocType,
  EnterItemProps2ValueEntry,
  PermissionName,
} from "../../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug } from "../../../auth/RestrictedByCapabilityAndDocumentPermissionByType.js";
import { type DocumentByType } from "../../../auth/RestrictedByDocumentPermissionByType.js";
import { ExpandCollapseIconButton } from "../../../commons/button/ExpandCollapseIconButton.js";
import { PropertiesManageModal } from "../modals/PropertiesManageModal.js";
import { ItemPropertyAddModal } from "../modals/PropertyAddModal.js";
import {
  getPropInputEntryKey,
  groupProperties,
  propToDefineInput,
} from "../properties.js";
import { PropertyField } from "../PropertyField.js";
import {
  PropertiesSection_ItemFragment,
  usePropertiesSection_DefineItemProps2Mutation,
  usePropertiesSection_DefineItemProps2OrderMutation,
  usePropertiesSection_EnterItemProps2ValueMutation,
} from "./PropertiesSection.generated.js";

export function PropertiesSection({
  projectId,
  docId,
  docType,
  itemId,
  item,
  readOnly = false,
  refetchQueries,
  ActionButtons,
  documentViewerPermissions,
}: {
  projectId: string | null;
  docId: string;
  docType: DocType;
  itemId: string;
  item: PropertiesSection_ItemFragment;
  readOnly?: boolean;
  refetchQueries?: InternalRefetchQueryDescriptor[];
  ActionButtons?: React.ReactNode;
  documentViewerPermissions: PermissionName[];
}) {
  const { t } = useTranslate(["QuoteItem"]);

  const document: DocumentByType = {
    docType,
    viewerPermissions: documentViewerPermissions,
  };

  const [isExpanded, setIsExpanded] = useLocalStorageAsState<boolean>(
    "msys-properties-expanded",
    false,
    true
  );

  const properties = React.useMemo(() => item.props2 ?? [], [item.props2]);
  const groups = React.useMemo(() => groupProperties(properties), [properties]);

  const client = useApolloClient();
  const [defineItemProps2] = usePropertiesSection_DefineItemProps2Mutation({
    client,
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [defineItemProps2Order] =
    usePropertiesSection_DefineItemProps2OrderMutation({
      client,
      refetchQueries,
      awaitRefetchQueries: true,
    });

  const addProperty = React.useCallback(
    async (property: DefineItemProps2Entry) => {
      if (
        properties.some(prop => prop.key === getPropInputEntryKey(property))
      ) {
        throw new Error(
          t(
            "A property already exists with this name. Please enter another name",
            { ns: "QuoteItem" }
          )
        );
      }
      await defineItemProps2({
        variables: {
          input: {
            projectId,
            docId,
            itemId,
            entries: [...properties.map(propToDefineInput), property],
          },
        },
      });
    },
    [t, defineItemProps2, docId, itemId, projectId, properties]
  );
  const defineProperties = React.useCallback(
    async (properties: DefineItemProps2Entry[]) => {
      await defineItemProps2({
        variables: {
          input: {
            projectId,
            docId,
            itemId,
            entries: properties,
          },
        },
      });
    },
    [defineItemProps2, docId, itemId, projectId]
  );
  const reorderProperties = React.useCallback(
    async (keys: string[]) => {
      await defineItemProps2Order({
        variables: {
          input: {
            projectId,
            docId,
            itemId,
            keys,
          },
        },
      });
    },
    [defineItemProps2Order, docId, itemId, projectId]
  );
  const setVisibility = React.useCallback(
    async (key: string, isVisible: boolean) => {
      const inputEntries = properties.map(property =>
        property.key === key
          ? propToDefineInput({ ...property, clientVisibility: isVisible })
          : propToDefineInput(property)
      );
      await defineProperties(inputEntries);
    },
    [defineProperties, properties]
  );
  const [enterItemProps2] = usePropertiesSection_EnterItemProps2ValueMutation({
    client,
  });

  const { expandedItemIds } = useOutletContext<{
    expandedItemIds: string[] | undefined;
  }>();

  const setPropertyValue = React.useCallback(
    async (property: EnterItemProps2ValueEntry) => {
      await enterItemProps2({
        variables: {
          input: {
            projectId,
            docId,
            itemId,
            entries: [property],
            expandedItemIds,
          },
        },
      });
    },
    [docId, enterItemProps2, itemId, projectId, expandedItemIds]
  );

  return (
    <CollapseSection
      title={t("Properties", { ns: "QuoteItem" })}
      titleVariant="h4"
      hideItemCountOnExpand={true}
      itemCount={properties.length}
      isInitiallyExpanded={properties.length > 0}
      ActionButtons={
        <Stack alignItems="center" direction={"row"} spacing={1}>
          {properties.length > 0 && (
            <ExpandCollapseIconButton
              isExpanded={isExpanded}
              setIsExpanded={setIsExpanded}
            />
          )}
          {ActionButtons}
          {!readOnly && (
            <ModalOpenButton
              Modal={PropertiesManageModal}
              modalProps={{
                projectId,
                docId,
                docType,
                itemId,
                itemTitle: item.title,
                itemIsRootItem: item.isRootItem,
                properties,
                defineProperties,
                reorderProperties,
                documentViewerPermissions,
              }}
            >
              <IconButton color={"primary"} size={"small"}>
                <EditIcon />
              </IconButton>
            </ModalOpenButton>
          )}
          {!readOnly && (
            <ModalOpenButton
              Modal={ItemPropertyAddModal}
              modalProps={{
                docType,
                projectId,
                docId,
                itemId,
                handleComplete: async property => {
                  await addProperty(property);
                },
              }}
            >
              <IconButton size="small" color="primary">
                <AddIcon />
              </IconButton>
            </ModalOpenButton>
          )}
        </Stack>
      }
    >
      {groups.length > 1 ? (
        groups.map(group => (
          <CollapseSection
            title={group.key || t("Non-grouped", { ns: "QuoteItem" })}
            titleVariant="body1"
            isInitiallyExpanded
            key={`properties-group-${group.key ?? "__other__"}`}
          >
            <Grid container spacing={1} columns={isExpanded ? 1 : 2}>
              {group.properties.map(property => (
                <Grid key={property.key} size={1}>
                  <PropertyField
                    projectId={projectId}
                    docId={docId}
                    itemId={itemId}
                    property={property}
                    setPropertyValue={setPropertyValue}
                    setVisibility={setVisibility}
                    readOnly={readOnly}
                    renderExpressionButton={button => (
                      <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                        capability="EXPRESSIONS"
                        permissionByType={{
                          QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                          REQUIREMENT: "MANAGE_REQUIREMENTS_EXPRESSIONS",
                          TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                          BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                        }}
                        document={document}
                      >
                        {button}
                      </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                    )}
                  />
                </Grid>
              ))}
            </Grid>
          </CollapseSection>
        ))
      ) : (
        <Grid container spacing={1} columns={isExpanded ? 1 : 2}>
          {properties.map(property => (
            <Grid key={property.key} size={1}>
              <PropertyField
                projectId={projectId}
                docId={docId}
                itemId={itemId}
                property={property}
                setPropertyValue={setPropertyValue}
                setVisibility={setVisibility}
                readOnly={readOnly}
                renderExpressionButton={button => (
                  <RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug
                    capability="EXPRESSIONS"
                    permissionByType={{
                      QUOTE: "MANAGE_QUOTES_EXPRESSIONS",
                      REQUIREMENT: "MANAGE_REQUIREMENTS_EXPRESSIONS",
                      TEMPLATE: "MANAGE_TEMPLATE_EXPRESSIONS",
                      BUILDING: "MANAGE_BUILDINGS_EXPRESSIONS",
                    }}
                    document={document}
                  >
                    {button}
                  </RestrictedByCapabilityAndDocumentPermissionByTypeWithDebug>
                )}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </CollapseSection>
  );
}
